import { useEffect, useMemo, useState, type ComponentType } from 'react';
import { Pause, Play, RotateCcw, Settings, Timer, X } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '~/components/ui/popover';
import NumberInputButton from '~/components/ui/number-input-button';

type PomodoroModalProps = { onClose: () => void };

const POMODORO_MODE = {
  FOCUS: 'focus',
  SHORT_BREAK: 'short_break',
  LONG_BREAK: 'long_break',
} as const;

type PomodoroMode = (typeof POMODORO_MODE)[keyof typeof POMODORO_MODE];

const TIMER_STATUS = {
  RUNNING: 'running',
  PAUSED: 'paused',
} as const;

type TimerStatus = (typeof TIMER_STATUS)[keyof typeof TIMER_STATUS];

type ModeDefinition = {
  value: PomodoroMode;
  label: string;
  Icon: ComponentType<{ className?: string }>;
};

type PomodoroConfig = {
  focusMinutes: number;
  shortBreakMinutes: number;
  longBreakMinutes: number;
};
type PomodoroConfigStorage = PomodoroConfig & {
  selectedMode: PomodoroMode;
};

const MODES: ModeDefinition[] = [
  { value: POMODORO_MODE.FOCUS, label: 'Foco', Icon: Timer },
  { value: POMODORO_MODE.SHORT_BREAK, label: 'Pausa curta', Icon: Pause },
  { value: POMODORO_MODE.LONG_BREAK, label: 'Pausa longa', Icon: Pause },
];

const STORAGE_KEY = 'pomodoro-config-v1';
const TIMER_STORAGE_KEY = 'pomodoro-timer-v1';
const DEFAULT_CONFIG: PomodoroConfig = {
  focusMinutes: 20,
  shortBreakMinutes: 5,
  longBreakMinutes: 20,
};

const MIN_MINUTES = 1;
const MAX_FOCUS_MINUTES = 90; // variações comuns como 90/20
const MAX_SHORT_BREAK_MINUTES = 90; // permite configurar pausas curtas maiores
const MAX_LONG_BREAK_MINUTES = MAX_FOCUS_MINUTES; // pausa longa igual ao foco

function clampInt(value: number, min: number, max: number) {
  if (!Number.isFinite(value)) return min;
  return Math.max(min, Math.min(max, Math.trunc(value)));
}

function pad2(value: number) {
  return String(value).padStart(2, '0');
}

function isPomodoroMode(value: unknown): value is PomodoroMode {
  return Object.values(POMODORO_MODE).includes(value as PomodoroMode);
}

function formatMMSS(totalSeconds: number) {
  const safe = Math.max(0, Math.trunc(totalSeconds));
  const minutes = Math.floor(safe / 60);
  const seconds = safe % 60;
  return `${pad2(minutes)}:${pad2(seconds)}`;
}

function formatHHmm(date: Date) {
  return `${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
}

function computeRangeLabel(totalSeconds: number, startDate: Date) {
  const endDate = new Date(startDate.getTime() + Math.max(0, totalSeconds) * 1000);
  return `${formatHHmm(startDate)} → ${formatHHmm(endDate)}`;
}

type PomodoroTimerStorageState = {
  mode: PomodoroMode;
  startAt: number;
  endAt: number | null;
  durationSeconds: number;
  status: TimerStatus;
  remainingSeconds?: number;
};

function readTimerStateFromStorage(): PomodoroTimerStorageState | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(TIMER_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PomodoroTimerStorageState>;
    const hasRequiredFields =
      Boolean(parsed.mode) &&
      typeof parsed.startAt === 'number' &&
      (typeof parsed.endAt === 'number' || parsed.endAt === null) &&
      (parsed.status === TIMER_STATUS.RUNNING || parsed.status === TIMER_STATUS.PAUSED);
    if (!hasRequiredFields) return null;
    if (typeof parsed.durationSeconds !== 'number') return null;
    if (parsed.status === TIMER_STATUS.PAUSED && typeof parsed.remainingSeconds !== 'number') {
      return null;
    }
    return parsed as PomodoroTimerStorageState;
  } catch {
    return null;
  }
}

function clearTimerState() {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(TIMER_STORAGE_KEY);
  } catch {
    // ignore
  }
}

function writeTimerStateToStorage(state: PomodoroTimerStorageState) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(TIMER_STORAGE_KEY, JSON.stringify(state));
  } catch {
    // ignore
  }
}

export default function PomodoroModal({ onClose }: PomodoroModalProps) {
  const [mode, setMode] = useState<PomodoroMode>(POMODORO_MODE.LONG_BREAK);
  const [config, setConfig] = useState<PomodoroConfig>(DEFAULT_CONFIG);
  const [hydratedConfig, setHydratedConfig] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [endAt, setEndAt] = useState<number | null>(null);
  const [durationSecondsAtStart, setDurationSecondsAtStart] = useState<number>(0);
  const [hydratedTimer, setHydratedTimer] = useState(false);
  const [pausedRemainingSeconds, setPausedRemainingSeconds] = useState<number | null>(null);

  const selectedMode = useMemo(() => MODES.find((m) => m.value === mode) ?? MODES[2], [mode]);

  const currentMinutes = useMemo(() => {
    if (mode === POMODORO_MODE.FOCUS) return config.focusMinutes;
    if (mode === POMODORO_MODE.SHORT_BREAK) return config.shortBreakMinutes;
    return config.longBreakMinutes;
  }, [config, mode]);

  const totalSeconds = currentMinutes * 60;

  const [remainingSeconds, setRemainingSeconds] = useState(totalSeconds);
  const [rangeLabel, setRangeLabel] = useState(() => computeRangeLabel(totalSeconds, new Date()));

  useEffect(() => {
    if (typeof window === 'undefined') return;

    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as Partial<PomodoroConfigStorage>;

        const focusMinutes = clampInt(
          parsed.focusMinutes ?? DEFAULT_CONFIG.focusMinutes,
          MIN_MINUTES,
          MAX_FOCUS_MINUTES,
        );
        const shortBreakMinutes = clampInt(
          parsed.shortBreakMinutes ?? DEFAULT_CONFIG.shortBreakMinutes,
          MIN_MINUTES,
          MAX_SHORT_BREAK_MINUTES,
        );

        setConfig({
          focusMinutes,
          shortBreakMinutes,
          longBreakMinutes: clampInt(
            parsed.longBreakMinutes ?? DEFAULT_CONFIG.longBreakMinutes,
            MIN_MINUTES,
            MAX_LONG_BREAK_MINUTES,
          ),
        });

        if (isPomodoroMode(parsed.selectedMode)) {
          setMode(parsed.selectedMode);
        }
      }
    } catch {
      // ignore
    } finally {
      setHydratedConfig(true);
    }
  }, []);

  useEffect(() => {
    if (!hydratedConfig) return;
    if (typeof window === 'undefined') return;

    try {
      window.localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          ...config,
          selectedMode: mode,
        } satisfies PomodoroConfigStorage),
      );
    } catch {
      // ignore
    }
  }, [config, hydratedConfig, mode]);

  useEffect(() => {
    const timerState = readTimerStateFromStorage();
    const now = Date.now();

    if (!timerState) {
      setHydratedTimer(true);
      return;
    }

    if (timerState.status === TIMER_STATUS.PAUSED) {
      setMode(timerState.mode);
      setIsRunning(false);
      setEndAt(null);
      setDurationSecondsAtStart(timerState.durationSeconds);
      setPausedRemainingSeconds(timerState.remainingSeconds ?? null);
      setRemainingSeconds(timerState.remainingSeconds ?? 0);
      setRangeLabel(computeRangeLabel(timerState.durationSeconds, new Date(timerState.startAt)));
      setHydratedTimer(true);
      return;
    }

    if (timerState.endAt === null || timerState.endAt <= now) {
      if (timerState) clearTimerState();
      setHydratedTimer(true);
      return;
    }

    setMode(timerState.mode);
    setIsRunning(true);
    setEndAt(timerState.endAt);
    setDurationSecondsAtStart(timerState.durationSeconds);
    setPausedRemainingSeconds(null);
    setRemainingSeconds(Math.max(0, Math.ceil((timerState.endAt - now) / 1000)));
    setRangeLabel(computeRangeLabel(timerState.durationSeconds, new Date(timerState.startAt)));
    setHydratedTimer(true);
  }, []);

  useEffect(() => {
    if (!hydratedTimer) return;
    if (isRunning) return;
    if (pausedRemainingSeconds !== null) return;

    setEndAt(null);
    setDurationSecondsAtStart(totalSeconds);
    setPausedRemainingSeconds(null);
    setRemainingSeconds(totalSeconds);
    setRangeLabel(computeRangeLabel(totalSeconds, new Date()));
  }, [hydratedTimer, isRunning, mode, pausedRemainingSeconds, totalSeconds]);

  useEffect(() => {
    if (!isRunning || endAt === null) return;

    const id = window.setInterval(() => {
      const now = Date.now();
      const remaining = Math.max(0, Math.ceil((endAt - now) / 1000));

      setRemainingSeconds(remaining);

      if (remaining <= 0) {
        setIsRunning(false);
        setEndAt(null);
        clearTimerState();
      }
    }, 100);

    return () => window.clearInterval(id);
  }, [isRunning, endAt]);

  const handleStart = () => {
    if (isRunning) return;
    const startAt = Date.now();
    const endTime = startAt + totalSeconds * 1000;

    setDurationSecondsAtStart(totalSeconds);
    setRangeLabel(computeRangeLabel(totalSeconds, new Date(startAt)));
    setRemainingSeconds(totalSeconds);
    setEndAt(endTime);
    setIsRunning(true);
    setPausedRemainingSeconds(null);
    writeTimerStateToStorage({
      mode,
      startAt,
      endAt: endTime,
      durationSeconds: totalSeconds,
      status: TIMER_STATUS.RUNNING,
    });
  };

  const handlePause = () => {
    if (!isRunning) return;
    setIsRunning(false);
    setEndAt(null);
    setPausedRemainingSeconds(remainingSeconds);
    writeTimerStateToStorage({
      mode,
      startAt: Date.now(),
      endAt: null,
      durationSeconds: durationSecondsAtStart || totalSeconds,
      status: TIMER_STATUS.PAUSED,
      remainingSeconds,
    });
  };

  const handleResume = () => {
    if (isRunning) return;
    if (pausedRemainingSeconds === null) return;
    const startAt = Date.now();
    const endTime = startAt + pausedRemainingSeconds * 1000;
    const durationSeconds = durationSecondsAtStart || totalSeconds;

    setDurationSecondsAtStart(durationSeconds);
    setRangeLabel(computeRangeLabel(durationSeconds, new Date(startAt)));
    setEndAt(endTime);
    setIsRunning(true);
    writeTimerStateToStorage({
      mode,
      startAt,
      endAt: endTime,
      durationSeconds,
      status: TIMER_STATUS.RUNNING,
    });
  };

  const handleReset = () => {
    setIsRunning(false);
    setEndAt(null);
    clearTimerState();
    setDurationSecondsAtStart(totalSeconds);
    setPausedRemainingSeconds(null);
    setRemainingSeconds(totalSeconds);
    setRangeLabel(computeRangeLabel(totalSeconds, new Date()));
  };

  const handleClose = () => {
    onClose();
  };

  const handleAdjustMinutes = (targetMode: PomodoroMode, deltaMinutes: number) => {
    setConfig((prev) => {
      if (targetMode === POMODORO_MODE.FOCUS) {
        return {
          ...prev,
          focusMinutes: clampInt(prev.focusMinutes + deltaMinutes, MIN_MINUTES, MAX_FOCUS_MINUTES),
        };
      }

      if (targetMode === POMODORO_MODE.SHORT_BREAK) {
        return {
          ...prev,
          shortBreakMinutes: clampInt(
            prev.shortBreakMinutes + deltaMinutes,
            MIN_MINUTES,
            MAX_SHORT_BREAK_MINUTES,
          ),
        };
      }

      return {
        ...prev,
        longBreakMinutes: clampInt(
          prev.longBreakMinutes + deltaMinutes,
          MIN_MINUTES,
          MAX_LONG_BREAK_MINUTES,
        ),
      };
    });
  };

  const progressPercent = useMemo(() => {
    const durationSeconds = isRunning ? durationSecondsAtStart : totalSeconds;
    if (durationSeconds <= 0) return 0;
    const elapsed = durationSeconds - remainingSeconds;
    return (elapsed / durationSeconds) * 100;
  }, [durationSecondsAtStart, isRunning, remainingSeconds, totalSeconds]);

  const hasPausedSession = pausedRemainingSeconds !== null;

  const primaryAction = () => {
    if (isRunning) {
      handlePause();
      return;
    }

    if (hasPausedSession) {
      handleResume();
      return;
    }

    handleStart();
  };

  const primaryActionText = () => {
    if (isRunning) {
      return 'Pausar';
    }

    if (hasPausedSession) {
      return 'Continuar';
    }

    return 'Iniciar';
  };

  const PrimaryActionIcon = isRunning ? Pause : Play;

  return (
    <div className="flex w-full flex-col">
      <div className="px-1 pt-1">
        <div className="flex items-start justify-between gap-3">
          <Select
            value={mode}
            onValueChange={(v) => setMode(v as PomodoroMode)}
            disabled={isRunning || pausedRemainingSeconds !== null}
          >
            <SelectTrigger
              aria-label="Selecionar modo do pomodoro"
              className="bg-transparent! shadow-none! rounded-md border-transparent h-9 w-auto min-w-0 p-2 text-text-primary transition-colors hover:bg-accent! active:bg-secondary! focus-visible:border-transparent focus-visible:ring-0 focus:ring-0 focus:outline-none justify-start gap-1 [&>svg]:size-5"
            >
              <SelectValue placeholder={selectedMode.label} />
            </SelectTrigger>
            <SelectContent className="w-[240px]">
              {MODES.map((m) => (
                <SelectItem key={m.value} value={m.value}>
                  <span className="inline-flex items-center gap-2">
                    <m.Icon className="h-4 w-4 text-text-primary shrink-0" aria-hidden="true" />
                    {m.label}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="flex items-center gap-1">
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  aria-label="Personalizar tempos do pomodoro"
                  title="Personalizar"
                  disabled={isRunning || pausedRemainingSeconds !== null}
                  aria-disabled={isRunning || pausedRemainingSeconds !== null}
                  className="inline-flex items-center justify-center transition-[background-color,color,border-color,box-shadow,transform] duration-200 ease-out motion-reduce:transition-none p-2 text-slate-700 rounded-md cursor-pointer hover:bg-accent active:bg-secondary active:scale-[0.98] disabled:opacity-50 disabled:pointer-events-none"
                >
                  <Settings className="h-5 w-5" aria-hidden="true" />
                </button>
              </PopoverTrigger>
              <PopoverContent
                sideOffset={10}
                align="end"
                className="w-[280px] rounded-[20px] border border-border-opaque bg-card p-3 shadow-md"
              >
                <div className="space-y-3">
                  <p className="text-sm font-semibold text-text-primary">Personalizar tempos</p>
                  <ul className="space-y-2">
                    <li className="flex items-center justify-between gap-3 py-1.5">
                      <p className="text-sm text-text-primary">Foco</p>
                      <div className="flex items-center gap-2">
                        <NumberInputButton
                          type="decrement"
                          onClick={() => handleAdjustMinutes('focus', -1)}
                          disabled={
                            isRunning ||
                            pausedRemainingSeconds !== null ||
                            config.focusMinutes <= MIN_MINUTES
                          }
                          className="h-11 w-11 md:h-7 md:w-7 rounded-[8px] border border-border-tertiary text-foreground bg-transparent hover:bg-accent active:bg-secondary disabled:bg-transparent disabled:text-text-disabled"
                        />
                        <span className="min-w-[62px] text-center text-sm font-semibold text-text-primary tabular-nums">
                          {config.focusMinutes} min
                        </span>
                        <NumberInputButton
                          type="increment"
                          onClick={() => handleAdjustMinutes('focus', +1)}
                          disabled={
                            isRunning ||
                            pausedRemainingSeconds !== null ||
                            config.focusMinutes >= MAX_FOCUS_MINUTES
                          }
                          className="h-11 w-11 md:h-7 md:w-7 rounded-[8px] border border-border-tertiary text-foreground bg-transparent hover:bg-accent active:bg-secondary disabled:bg-transparent disabled:text-text-disabled"
                        />
                      </div>
                    </li>

                    <li className="flex items-center justify-between gap-3 py-1.5">
                      <p className="text-sm text-text-primary">Pausa curta</p>
                      <div className="flex items-center gap-2">
                        <NumberInputButton
                          type="decrement"
                          onClick={() => handleAdjustMinutes('short_break', -1)}
                          disabled={
                            isRunning ||
                            pausedRemainingSeconds !== null ||
                            config.shortBreakMinutes <= MIN_MINUTES
                          }
                          className="h-11 w-11 md:h-7 md:w-7 rounded-[8px] border border-border-tertiary text-foreground bg-transparent hover:bg-accent active:bg-secondary disabled:bg-transparent disabled:text-text-disabled"
                        />
                        <span className="min-w-[62px] text-center text-sm font-semibold text-text-primary tabular-nums">
                          {config.shortBreakMinutes} min
                        </span>
                        <NumberInputButton
                          type="increment"
                          onClick={() => handleAdjustMinutes('short_break', +1)}
                          disabled={
                            isRunning ||
                            pausedRemainingSeconds !== null ||
                            config.shortBreakMinutes >= MAX_SHORT_BREAK_MINUTES
                          }
                          className="h-11 w-11 md:h-7 md:w-7 rounded-[8px] border border-border-tertiary text-foreground bg-transparent hover:bg-accent active:bg-secondary disabled:bg-transparent disabled:text-text-disabled"
                        />
                      </div>
                    </li>

                    <li className="flex items-center justify-between gap-3 py-1.5">
                      <p className="text-sm text-text-primary">Pausa longa</p>
                      <div className="flex items-center gap-2">
                        <NumberInputButton
                          type="decrement"
                          onClick={() => handleAdjustMinutes('long_break', -1)}
                          disabled={
                            isRunning ||
                            pausedRemainingSeconds !== null ||
                            config.longBreakMinutes <= MIN_MINUTES
                          }
                          className="h-11 w-11 md:h-7 md:w-7 rounded-[8px] border border-border-tertiary text-foreground bg-transparent hover:bg-accent active:bg-secondary disabled:bg-transparent disabled:text-text-disabled"
                        />
                        <span className="min-w-[62px] text-center text-sm font-semibold text-text-primary tabular-nums">
                          {config.longBreakMinutes} min
                        </span>
                        <NumberInputButton
                          type="increment"
                          onClick={() => handleAdjustMinutes('long_break', +1)}
                          disabled={
                            isRunning ||
                            pausedRemainingSeconds !== null ||
                            config.longBreakMinutes >= MAX_LONG_BREAK_MINUTES
                          }
                          className="h-11 w-11 md:h-7 md:w-7 rounded-[8px] border border-border-tertiary text-foreground bg-transparent hover:bg-accent active:bg-secondary disabled:bg-transparent disabled:text-text-disabled"
                        />
                      </div>
                    </li>
                  </ul>
                </div>
              </PopoverContent>
            </Popover>

            <button
              type="button"
              aria-label="Fechar pomodoro"
              title="Fechar"
              onClick={handleClose}
              className="inline-flex items-center justify-center transition-[background-color,color,border-color,box-shadow,transform] duration-200 ease-out motion-reduce:transition-none p-2 text-slate-700 rounded-md cursor-pointer hover:bg-accent active:bg-secondary active:scale-[0.98]"
            >
              <X className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>

      <div className="mx-auto flex w-full max-w-[320px] flex-col gap-4 px-4 pb-4 pt-3">
        <div className="text-center">
          <p className="text-[48px] font-bold leading-none tabular-nums text-text-primary">
            {formatMMSS(remainingSeconds)}
          </p>
          <p className="mt-3 text-sm tabular-nums text-text-secondary text-pretty">{rangeLabel}</p>

          <div
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            className="bg-bg-core/20 relative overflow-hidden rounded-full mt-4 h-2.5 w-full"
          >
            <div
              className="bg-bg-core h-full transition-[width] duration-300 ease-linear"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-center gap-2">
          <button
            type="button"
            className="inline-flex items-center justify-center whitespace-nowrap cursor-pointer transition-all duration-200 ease-out hover:brightness-90 active:scale-[0.98] disabled:opacity-60 disabled:pointer-events-none bg-bg-core-dark text-white min-h-[44px] min-w-[60px] py-2.5 px-3 gap-2 rounded-[8px] text-sm font-medium"
            onClick={primaryAction}
          >
            <PrimaryActionIcon className="h-4 w-4" aria-hidden="true" />
            {primaryActionText()}
          </button>

          <button
            type="button"
            className="inline-flex items-center justify-center whitespace-nowrap cursor-pointer transition-all duration-200 ease-out hover:bg-bg-secondary active:scale-[0.98] disabled:opacity-60 disabled:pointer-events-none border border-border-tertiary text-text-primary min-h-[44px] min-w-[60px] py-2.5 px-3 gap-2 rounded-[8px] text-sm font-medium bg-transparent"
            onClick={handleReset}
          >
            <RotateCcw className="h-4 w-4" aria-hidden="true" />
            Resetar
          </button>
        </div>
        <p className="sr-only">{selectedMode.label}</p>
      </div>
    </div>
  );
}
